import styled from 'styled-components';
import { FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import { useEffect, useMemo, useState } from 'react';
import { MAX_BOATS } from 'utils/sort';
import { useAtomValue } from 'jotai';
import { Text } from 'components/primitives/typography';
import {
  areaParamAtom,
  areBoatsLoadingAtom,
  DSUQueryNameAtom,
  isAnyFilterActiveAtom,
  isRouterReadyAtom,
  nearParamAtom,
  otherQueryParamsAtom,
  totalBoatsCountAtom,
} from './Filter/filterStore';
import { useBoatDisplayedTrack } from './hooks/useBoatDisplayedTrack';
import { GeoBoundaries } from './GeoBoundaries';

export const composeHeadline = (headline) => (headline ? headline.replace(', USA', '') : 'this area');

const emptyText = (searchLocation: string, isAnyFilterActive: boolean) =>
  isAnyFilterActive
    ? `No boats found in ${searchLocation} that match your criteria. `
    : `No boats found in ${searchLocation}. `;

export default function BoatsListHeader(props) {
  const near = useAtomValue(nearParamAtom);
  const area = useAtomValue(areaParamAtom);
  const total = useAtomValue(totalBoatsCountAtom);
  const otherParams = useAtomValue<any>(otherQueryParamsAtom);
  const DSUQueryName = useAtomValue(DSUQueryNameAtom);
  const isAnyFilterActive = useAtomValue(isAnyFilterActiveAtom);
  const areBoatsLoading = useAtomValue(areBoatsLoadingAtom);
  const routerReady = useAtomValue(isRouterReadyAtom);

  const areaName = useMemo(() => GeoBoundaries.find((boundary) => boundary.Area === area)?.AreaName, [area]);

  const headline = composeHeadline(areaName || near || DSUQueryName);

  const [headlineStatic, setHeadlineStatic] = useState('');

  const searchHeadline = useMemo(() => {
    const localityTotal = otherParams.locality ? total : Math.min(total, MAX_BOATS);

    const totalBoatsText = areBoatsLoading
      ? 'Searching'
      : `${localityTotal}${total > MAX_BOATS && !otherParams.locality ? '+' : ''}`;

    if (!areBoatsLoading && total === 0) {
      return emptyText(headline, isAnyFilterActive);
    }
    if (near || DSUQueryName) {
      return `${totalBoatsText} boat${localityTotal === 1 ? '' : 's'} in ${headline}`;
    }
    return `${totalBoatsText} boat${localityTotal === 1 ? '' : 's'} in this area`;
  }, [headline, DSUQueryName, total, near, areBoatsLoading]);

  useEffect(() => {
    if (areBoatsLoading) return;

    setHeadlineStatic(searchHeadline);
  }, [searchHeadline, areBoatsLoading]);

  useBoatDisplayedTrack(routerReady, areBoatsLoading, headlineStatic);

  return (
    <BoatsHeader px="4px" mb="18px" gap="36px" mdMin={{ mb: '28px' }} {...props}>
      <Text data-testid="search-heading" fWeight="700" fSize="18px" mdMin={{ fSize: '20px' }}>
        {searchHeadline}
      </Text>

      {/* Removing Sort By component based on SRCH-154 */}
      {/* {!smallScreen && <SortingFilterButton />} */}
    </BoatsHeader>
  );
}

const BoatsHeader = styled(FlexAlignCenterSpaceBetween)``;
